import type {
  AuditLogEntry,
  CellLine,
  ChatApprovalContract,
  ChatContract,
  CopilotConversation,
  CopilotMessage,
  DriftAlert,
  LotAssignment,
  MarkerResult,
  OutcomeInstrument,
  OutcomeInstrumentType,
  OutcomeMeasurementSource,
  OutcomeSignal,
  OutcomeSignalStatus,
  Patient,
  QCEntry,
  QCEntryType,
  QCMarker,
  Report,
} from '@lineageatlas/shared';

const TOKEN_KEY = 'lineageatlas_token';
const API_BASE = import.meta.env.VITE_API_BASE ?? '';

export type CountMeta = { count: number };

export interface ApiResponse<T, M = undefined> {
  data: T;
  meta?: M;
}

export interface ApiErrorBody {
  error?: string;
  feature?: string;
  feature_disabled?: boolean;
}

export class RegenLoopApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | null;
  readonly feature: string | null;
  readonly featureDisabled: boolean;

  constructor(message: string, status: number, body: ApiErrorBody | null = null) {
    super(message);
    this.name = 'RegenLoopApiError';
    this.status = status;
    this.body = body;
    this.feature = body?.feature ?? null;
    this.featureDisabled = body?.feature_disabled === true;
  }
}

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue>;

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: QueryParams;
}

export type ApiQCResult = QCEntry['overall_result'] | 'borderline';

export type LotWithLatestQc = CellLine & {
  latest_qc_result: ApiQCResult | null;
  latest_qc_date: string | null;
};

export type LotFilters = {
  status?: string;
  cell_type?: string;
  supplier?: string;
};

export type CreateLotInput = {
  lot_number: string;
  name?: string;
  cell_type: string;
  supplier?: string;
  collection_date?: string;
  vial_count: number;
  storage_location?: string;
  notes?: string;
};

export type UpdateLotInput = Partial<{
  lot_number: string;
  cell_type: string;
  supplier: string | null;
  collection_date: string | null;
  vials_remaining: number;
  storage_location: string | null;
  notes: string | null;
  status: string;
}>;

export type ReleaseReadiness = {
  ready: boolean;
  blocked: boolean;
  checklist: Array<{
    test: string;
    required: boolean;
    completed: boolean;
    passed: boolean | null;
    entry_id: string | null;
  }>;
  missing_tests: string[];
  failed_tests: string[];
};

export type CoAMarkerResult = {
  marker: string;
  value: number;
  unit: string;
  specification: string;
  result: 'PASS' | 'FAIL';
  comment: string;
};

export type CoAContent = {
  executive_summary: string;
  lot_assessment: string;
  marker_analysis: CoAMarkerResult[];
  risk_narrative: string;
  release_recommendation: 'RELEASE' | 'HOLD' | 'QUARANTINE';
  recommendation_rationale: string;
  regulatory_notes: string;
};

export type CoAMarker = {
  marker_name: string;
  value: number;
  unit: string;
  result: string;
  expected_min?: number | null;
  expected_max?: number | null;
  reference_source?: string | null;
};

export type CoAGeneration = {
  report_id: string;
  lot_id: string;
  generated_at: string;
  coa_content: CoAContent;
  lot: Pick<
    CellLine,
    | 'id'
    | 'lot_number'
    | 'cell_type'
    | 'supplier'
    | 'collection_date'
    | 'date_received'
    | 'storage_location'
    | 'current_passage'
    | 'status'
  >;
  clinic: {
    name: string;
    contact_email: string | null;
  };
  qc_entries: QCHistoryEntry[];
  markers: CoAMarker[];
};

export type MarkerTrend = {
  marker: string;
  current_value: number;
  trend: 'stable' | 'declining' | 'rising';
  predicted_threshold_passage: number | null;
  severity: 'low' | 'medium' | 'high';
};

export type DriftPrediction = {
  predicted_safe_passages: number;
  markers_at_risk: MarkerTrend[];
  overall_risk: 'low' | 'medium' | 'high';
  summary: string;
  recommendations: string[];
};

export type PubMedArticle = {
  pmid: string;
  title: string;
  authors: string;
  journal: string;
  pubdate: string;
  doi: string;
};

export type LiteratureContext = {
  marker: string;
  value: number;
  threshold: number;
  context_note: string;
  relevant_articles: Array<{
    pmid: string;
    title: string;
    relevance: string;
  }>;
};

export type LiteratureContextResponse = {
  contexts: LiteratureContext[];
  articles: PubMedArticle[];
};

export type PatientLot = CellLine & {
  administered_at: string;
  vials_used: number;
  administered_by: string | null;
};

export type CreatePatientInput = {
  patient_code: string;
  indication: string;
  notes?: string;
};

export type LotAssignmentWithPatient = LotAssignment & {
  patient_code: string;
  indication: string | null;
};

export type LotAssignmentWithJoins = LotAssignmentWithPatient & {
  lot_number: string;
  cell_type: string;
};

export type CreateAssignmentInput = {
  lot_id: string;
  patient_id: string;
  vials_used: number;
  administered_at?: string;
  notes?: string;
};

export type InventoryLot = Pick<
  CellLine,
  | 'id'
  | 'lot_number'
  | 'cell_type'
  | 'supplier'
  | 'vial_count'
  | 'vials_remaining'
  | 'storage_location'
  | 'status'
  | 'updated_at'
>;

export type LowStockLot = Pick<
  CellLine,
  'id' | 'lot_number' | 'cell_type' | 'supplier' | 'vials_remaining' | 'storage_location' | 'status'
>;

export type LowStockMeta = CountMeta & { threshold: number };

export type DriftAlertWithLot = DriftAlert & {
  lot_number: string;
  cell_type: string;
};

export type AcknowledgeAlertResult = {
  id: string;
  acknowledged: true;
};

export type AuditLogFilters = {
  entity_type?: string;
  user_id?: string;
  action?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
};

export type AuditLogEntryWithUserEmail = AuditLogEntry & {
  user_email: string | null;
};

export type AuditLogMeta = CountMeta & {
  limit: number;
  offset: number;
};

export type ReportType = Report['report_type'];

export type CreateReportInput = {
  report_type: ReportType;
  cell_line_id?: string;
};

export type QueuedReport = Pick<Report, 'id' | 'report_type' | 'status' | 'cell_line_id'> & {
  status: 'queued';
};

export type CompareMarker = QCMarker & {
  lot_id: string;
  performed_at: string | null;
  overall_result: ApiQCResult;
};

export type CompareResult = {
  lots: LotWithLatestQc[];
  markers_by_lot: Record<string, CompareMarker[]>;
  marker_names: string[];
};

export type CompareMeta = {
  compared_lots: number;
  total_markers: number;
};

export type QCEntryApi = Omit<QCEntry, 'overall_result'> & {
  overall_result: ApiQCResult;
  ai_analysis?: unknown | null;
};

export type QCHistoryEntry = QCEntryApi;

export type QCEntryDetail = QCEntryApi & {
  markers: QCMarker[];
};

export type QCMarkerInput = {
  marker_name: string;
  value: number;
  unit: string;
};

export type CreateQCEntryInput = {
  lot_id: string;
  entry_type?: QCEntryType;
  passage_number?: number;
  markers: QCMarkerInput[];
  notes?: string;
  performed_at?: string;
};

export type CreatedQCMarker = {
  id: string;
  marker_name: string;
  value: number;
  unit: string;
  result: MarkerResult;
  expected_min: number | null;
  expected_max: number | null;
  reference_source: string | null;
};

export type CreatedQCEntry = {
  id: string;
  lot_id: string;
  overall_result: ApiQCResult;
  performed_by: string;
  performed_at: string;
  markers: CreatedQCMarker[];
  drift_alerts: unknown[];
  ai_analysis: unknown | null;
};

export type OutcomeInstrumentInput = {
  code: string;
  name: string;
  instrument_type: OutcomeInstrumentType;
  scale_min?: number | null;
  scale_max?: number | null;
  unit?: string | null;
  higher_is_better?: boolean;
  description?: string | null;
};

export type OutcomeMeasurementWithJoins = {
  id: string;
  clinic_id: string;
  patient_id: string;
  assignment_id: string | null;
  instrument_id: string;
  measured_at: string;
  timepoint_label: string | null;
  value_numeric: number | null;
  value_text: string | null;
  value_json: string | null;
  source: OutcomeMeasurementSource;
  entered_by: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  instrument_code: string;
  instrument_name: string;
  higher_is_better?: 0 | 1;
  patient_code: string;
  lot_id?: string | null;
  lot_number?: string | null;
};

export type OutcomeMeasurementsFilters = {
  patient_id?: string;
  assignment_id?: string;
  instrument_id?: string;
};

export type OutcomeMeasurementInput = {
  patient_id: string;
  assignment_id?: string | null;
  instrument_id: string;
  measured_at: string;
  timepoint_label?: string | null;
  value_numeric?: number | null;
  value_text?: string | null;
  value_json?: unknown;
  source?: OutcomeMeasurementSource;
  notes?: string | null;
};

export type OutcomeSignalsMeta = CountMeta & {
  generated: number;
  evaluated_groups: number;
  copy: string;
};

export type OutcomeSignalFilters = {
  status?: OutcomeSignalStatus;
};

export type UpdateOutcomeSignalInput = {
  status: OutcomeSignalStatus;
  dismissal_reason?: string | null;
};

export type CopilotConversationListOptions = {
  include_archived?: boolean;
};

export type CopilotConversationDetail = {
  conversation: CopilotConversation;
  messages: CopilotMessage[];
};

export type CopilotChatInput = {
  conversation_id?: string;
  message: string;
  intent?: 'answer' | 'draft';
  title?: string;
};

export type CopilotChatResult = {
  conversation_id: string;
  user_message_id: string;
  assistant_message_id: string;
  contract: ChatContract;
};

export type CopilotApprovalInput = {
  conversation_id: string;
  draft_message_id: string;
  review_state: ChatApprovalContract['review_state'];
  decision_reason?: string | null;
  approved_payload_hash?: string | null;
};

export type CopilotApprovalResult = {
  contract: ChatApprovalContract;
};

function getToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

function buildHeaders(headers?: HeadersInit): HeadersInit {
  const token = getToken();
  const baseHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    baseHeaders['Authorization'] = `Bearer ${token}`;
  }
  return {
    ...baseHeaders,
    ...(headers as Record<string, string> | undefined),
  };
}

function appendQuery(path: string, params?: QueryParams): string {
  if (!params) return path;
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return (
    value != null &&
    typeof value === 'object' &&
    ('error' in value || 'feature_disabled' in value || 'feature' in value)
  );
}

function errorMessage(status: number, body: ApiErrorBody | null): string {
  if (status === 401) return 'Unauthorized - please log in';
  if (body?.error) return body.error;
  return `API error: ${status}`;
}

export function isRegenLoopApiError(error: unknown): error is RegenLoopApiError {
  return error instanceof RegenLoopApiError;
}

export function isFeatureDisabledError(
  error: unknown,
): error is RegenLoopApiError & { featureDisabled: true } {
  return error instanceof RegenLoopApiError && error.featureDisabled;
}

export async function regenloopRequest<T, M = undefined>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<ApiResponse<T, M>> {
  const { body, query, headers, ...init } = options;
  const res = await fetch(`${API_BASE}${appendQuery(path, query)}`, {
    ...init,
    headers: buildHeaders(headers),
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const json = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const errorBody = isApiErrorBody(json) ? json : null;
    throw new RegenLoopApiError(errorMessage(res.status, errorBody), res.status, errorBody);
  }

  return json as ApiResponse<T, M>;
}

export function listOutcomeInstruments() {
  return regenloopRequest<OutcomeInstrument[], CountMeta>('/api/v1/outcomes/instruments');
}

export function createOutcomeInstrument(body: OutcomeInstrumentInput) {
  return regenloopRequest<OutcomeInstrument>('/api/v1/outcomes/instruments', {
    method: 'POST',
    body,
  });
}

export function listOutcomeMeasurements(filters: OutcomeMeasurementsFilters = {}) {
  return regenloopRequest<OutcomeMeasurementWithJoins[], CountMeta>('/api/v1/outcomes/measurements', {
    query: filters,
  });
}

export function createOutcomeMeasurement(body: OutcomeMeasurementInput) {
  return regenloopRequest<OutcomeMeasurementWithJoins>('/api/v1/outcomes/measurements', {
    method: 'POST',
    body,
  });
}

export function listOutcomeSignals(filters: OutcomeSignalFilters = {}) {
  return regenloopRequest<OutcomeSignal[], OutcomeSignalsMeta>('/api/v1/signals', {
    query: filters,
  });
}

export function updateOutcomeSignal(id: string, body: UpdateOutcomeSignalInput) {
  return regenloopRequest<OutcomeSignal | undefined>(`/api/v1/signals/${id}`, {
    method: 'PATCH',
    body,
  });
}

export function reviewOutcomeSignal(id: string) {
  return updateOutcomeSignal(id, { status: 'reviewed' });
}

export function dismissOutcomeSignal(id: string, dismissal_reason?: string | null) {
  return updateOutcomeSignal(id, { status: 'dismissed', dismissal_reason });
}

export function restoreOutcomeSignalCandidate(id: string) {
  return updateOutcomeSignal(id, { status: 'candidate', dismissal_reason: null });
}

export function listCopilotConversations(options: CopilotConversationListOptions = {}) {
  return regenloopRequest<CopilotConversation[], CountMeta>('/api/v1/copilot/conversations', {
    query: { include_archived: options.include_archived ? 'true' : undefined },
  });
}

export function getCopilotConversation(id: string) {
  return regenloopRequest<CopilotConversationDetail>(`/api/v1/copilot/conversations/${id}`);
}

export function sendCopilotChat(body: CopilotChatInput) {
  return regenloopRequest<CopilotChatResult>('/api/v1/copilot/chat', {
    method: 'POST',
    body,
  });
}

export function createCopilotApproval(body: CopilotApprovalInput) {
  return regenloopRequest<CopilotApprovalResult>('/api/v1/copilot/approvals', {
    method: 'POST',
    body,
  });
}

export function archiveCopilotConversation(id: string) {
  return regenloopRequest<CopilotConversation>(`/api/v1/copilot/conversations/${id}/archive`, {
    method: 'POST',
  });
}

export function restoreCopilotConversation(id: string) {
  return regenloopRequest<CopilotConversation>(`/api/v1/copilot/conversations/${id}/restore`, {
    method: 'POST',
  });
}

export function listLots(filters: LotFilters = {}) {
  return regenloopRequest<LotWithLatestQc[], CountMeta>('/api/v1/lots', { query: filters });
}

export function createLot(body: CreateLotInput) {
  return regenloopRequest<LotWithLatestQc>('/api/v1/lots', { method: 'POST', body });
}

export function getLot(id: string) {
  return regenloopRequest<LotWithLatestQc>(`/api/v1/lots/${id}`);
}

export function updateLot(id: string, body: UpdateLotInput) {
  return regenloopRequest<LotWithLatestQc>(`/api/v1/lots/${id}`, { method: 'PATCH', body });
}

export function releaseLot(id: string) {
  return regenloopRequest<LotWithLatestQc>(`/api/v1/lots/${id}/release`, { method: 'POST' });
}

export function quarantineLot(id: string, reason: string) {
  return regenloopRequest<LotWithLatestQc>(`/api/v1/lots/${id}/quarantine`, {
    method: 'POST',
    body: { reason },
  });
}

export function listLotQcHistory(id: string) {
  return regenloopRequest<QCHistoryEntry[], CountMeta>(`/api/v1/lots/${id}/qc-history`);
}

export function listLotAssignments(id: string) {
  return regenloopRequest<LotAssignmentWithPatient[], CountMeta>(`/api/v1/lots/${id}/assignments`);
}

export function getLotReleaseReadiness(id: string) {
  return regenloopRequest<ReleaseReadiness>(`/api/v1/lots/${id}/release-readiness`);
}

export function generateLotCoa(id: string) {
  return regenloopRequest<CoAGeneration>(`/api/v1/lots/${id}/generate-coa`, { method: 'POST' });
}

export function listLotAlerts(id: string) {
  return regenloopRequest<DriftAlert[], CountMeta>(`/api/v1/lots/${id}/alerts`);
}

export function getLotPrediction(id: string) {
  return regenloopRequest<DriftPrediction>(`/api/v1/lots/${id}/prediction`);
}

export function getLotLiteratureContext(id: string) {
  return regenloopRequest<LiteratureContextResponse>(`/api/v1/lots/${id}/literature-context`);
}

export function listPatients() {
  return regenloopRequest<Patient[], CountMeta>('/api/v1/patients');
}

export function createPatient(body: CreatePatientInput) {
  return regenloopRequest<Patient>('/api/v1/patients', { method: 'POST', body });
}

export function getPatient(id: string) {
  return regenloopRequest<Patient>(`/api/v1/patients/${id}`);
}

export function listPatientLots(id: string) {
  return regenloopRequest<PatientLot[], CountMeta>(`/api/v1/patients/${id}/lots`);
}

export function createAssignment(body: CreateAssignmentInput) {
  return regenloopRequest<LotAssignmentWithJoins>('/api/v1/assignments', {
    method: 'POST',
    body,
  });
}

export function getAssignment(id: string) {
  return regenloopRequest<LotAssignmentWithJoins>(`/api/v1/assignments/${id}`);
}

export function listInventory() {
  return regenloopRequest<InventoryLot[], CountMeta>('/api/v1/inventory');
}

export function listLowStockInventory(threshold?: number) {
  return regenloopRequest<LowStockLot[], LowStockMeta>('/api/v1/inventory/low-stock', {
    query: { threshold },
  });
}

export function listAlerts() {
  return regenloopRequest<DriftAlertWithLot[], CountMeta>('/api/v1/alerts');
}

export function acknowledgeAlert(id: string) {
  return regenloopRequest<AcknowledgeAlertResult>(`/api/v1/alerts/${id}/acknowledge`, {
    method: 'PATCH',
  });
}

export function listAuditLog(filters: AuditLogFilters = {}) {
  return regenloopRequest<AuditLogEntryWithUserEmail[], AuditLogMeta>('/api/v1/audit-log', {
    query: filters,
  });
}

export function listReports() {
  return regenloopRequest<Report[], CountMeta>('/api/v1/reports');
}

export function getReport(id: string) {
  return regenloopRequest<Report>(`/api/v1/reports/${id}`);
}

export function createReport(body: CreateReportInput) {
  return regenloopRequest<QueuedReport>('/api/v1/reports', { method: 'POST', body });
}

export function compareLots(lot_ids: [string, string] | [string, string, string]) {
  return regenloopRequest<CompareResult, CompareMeta>('/api/v1/compare', {
    method: 'POST',
    body: { lot_ids },
  });
}

export function createQCEntry(body: CreateQCEntryInput) {
  return regenloopRequest<CreatedQCEntry>('/api/v1/qc-entries', { method: 'POST', body });
}

export function getQCEntry(id: string) {
  return regenloopRequest<QCEntryDetail>(`/api/v1/qc-entries/${id}`);
}
